import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Package, Clock, CheckCircle, AlertCircle, RefreshCw, 
  Truck, Eye, Send
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const GoodsTransferShipmentRequests = () => {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [confirmingShipment, setConfirmingShipment] = useState(null);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/internal-transfer/shipment-requests', {
        params: { from_section: 'Goods Transfer' }
      });
      setRequests(response.data.transfers || []);
    } catch (error) {
      console.error('Failed to fetch shipment requests:', error);
      toast.error('Failed to load shipment requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  const handleConfirmShipment = async (transferId) => {
    setConfirmingShipment(transferId);
    try {
      await apiClient.post('/internal-transfer/confirm-shipment', {
        transfer_id: transferId
      });
      toast.success('Shipment confirmed successfully');
      fetchRequests();
      setShowDetailDialog(false);
    } catch (error) {
      console.error('Error confirming shipment:', error);
      toast.error(error.response?.data?.detail || 'Failed to confirm shipment');
    } finally {
      setConfirmingShipment(null);
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      requested: 'bg-orange-100 text-orange-700 border-orange-200',
      shipped: 'bg-blue-100 text-blue-700 border-blue-200',
      received: 'bg-green-100 text-green-700 border-green-200'
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/sections/goods-transfer')} 
              className="text-[#F9FAFB] hover:bg-white/10"
              data-testid="back-button"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <Truck className="w-6 h-6 text-[#E8DCC8]" />
              <div>
                <h1 className="text-xl font-bold font-display">Shipment Requests</h1>
                <p className="text-sm text-[#E8DCC8]">Internal Transfer - Goods Transfer Section</p>
              </div>
            </div>
          </div>
          <Button 
            variant="ghost" 
            onClick={fetchRequests}
            className="text-[#F9FAFB] hover:bg-white/10"
            data-testid="refresh-button"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
          <Card className="border-orange-200 bg-orange-50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-orange-600">Pending Shipment</p>
                  <p className="text-2xl font-bold text-orange-700">{requests.length}</p>
                </div>
                <Clock className="w-8 h-8 text-orange-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-blue-200 bg-blue-50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-600">Total Bundles</p>
                  <p className="text-2xl font-bold text-blue-700">
                    {requests.reduce((sum, r) => sum + (r.bundle_count || 0), 0)}
                  </p>
                </div>
                <Package className="w-8 h-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-green-200 bg-green-50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-600">Destinations</p>
                  <p className="text-2xl font-bold text-green-700">
                    {new Set(requests.map(r => r.to_section)).size}
                  </p>
                </div>
                <Send className="w-8 h-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Info Banner */}
        <Card className="mb-6 border-blue-200 bg-blue-50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <p className="text-sm text-blue-800 font-medium">
                  These are transfers that have been requested by destination sections.
                </p>
                <p className="text-xs text-blue-600 mt-1">
                  Click "Confirm Shipment" to notify the destination that bundles are on the way.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Requests List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
          </div>
        ) : requests.length === 0 ? (
          <Card className="py-12">
            <CardContent className="flex flex-col items-center justify-center text-center">
              <Truck className="w-16 h-16 text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-600 mb-1">No Pending Shipment Requests</h3>
              <p className="text-sm text-gray-500">
                All transfers have been shipped or no consignments have been requested yet.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {requests.map(request => (
              <Card 
                key={request.transfer_id} 
                className="hover:shadow-md transition-shadow border-l-4 border-l-orange-400"
                data-testid={`shipment-request-${request.transfer_id}`}
              >
                <CardContent className="p-5">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <Badge variant="outline" className="bg-orange-100 text-orange-700 border-orange-300 font-mono">
                          {request.transfer_id}
                        </Badge>
                        <span className="text-gray-400">→</span>
                        <Badge className="bg-purple-100 text-purple-700 border-0">
                          {request.to_section}
                        </Badge>
                        <Badge className={getStatusBadge(request.status)}>
                          {request.status?.toUpperCase()}
                        </Badge>
                      </div>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <p className="text-gray-500">Bundles</p>
                          <p className="font-semibold">{request.bundle_count || request.bundle_codes?.length || 0}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Requested By</p>
                          <p className="font-semibold">{request.requested_by_name || '-'}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Created</p>
                          <p className="font-semibold">
                            {request.created_at ? new Date(request.created_at).toLocaleDateString() : '-'}
                          </p>
                        </div>
                        <div>
                          <p className="text-gray-500">Requested At</p>
                          <p className="font-semibold">
                            {request.requested_at ? new Date(request.requested_at).toLocaleString() : '-'}
                          </p>
                        </div>
                      </div>

                      {/* Bundle preview */}
                      {request.bundle_codes && request.bundle_codes.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-2">
                          {request.bundle_codes.slice(0, 5).map((bundle, idx) => (
                            <Badge key={idx} variant="outline" className="text-xs font-mono">
                              {bundle}
                            </Badge>
                          ))}
                          {request.bundle_codes.length > 5 && (
                            <Badge variant="outline" className="text-xs">
                              +{request.bundle_codes.length - 5} more
                            </Badge>
                          )}
                        </div>
                      )}
                    </div>
                    
                    <div className="flex gap-2 ml-4">
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => {
                          setSelectedRequest(request);
                          setShowDetailDialog(true);
                        }}
                        data-testid={`view-details-${request.transfer_id}`}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        View
                      </Button>
                      <Button 
                        size="sm"
                        className="bg-green-600 hover:bg-green-700"
                        onClick={() => handleConfirmShipment(request.transfer_id)}
                        disabled={confirmingShipment === request.transfer_id}
                        data-testid={`confirm-shipment-${request.transfer_id}`}
                      >
                        {confirmingShipment === request.transfer_id ? (
                          <>Confirming...</>
                        ) : (
                          <>
                            <Truck className="w-4 h-4 mr-1" />
                            Confirm Shipment
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Truck className="w-5 h-5" />
              Transfer Details: {selectedRequest?.transfer_id}
            </DialogTitle>
          </DialogHeader>
          
          {selectedRequest && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-sm text-gray-500">From Section</p>
                  <p className="font-medium">{selectedRequest.from_section}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">To Section</p>
                  <p className="font-medium text-purple-700">{selectedRequest.to_section}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Status</p>
                  <Badge className={getStatusBadge(selectedRequest.status)}>
                    {selectedRequest.status?.toUpperCase()}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Bundle Count</p>
                  <p className="font-medium">{selectedRequest.bundle_count || selectedRequest.bundle_codes?.length || 0}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Staff</p>
                  <p className="font-medium">{selectedRequest.staff_name} ({selectedRequest.staff_id})</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Requested By</p>
                  <p className="font-medium">{selectedRequest.requested_by_name || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Created At</p>
                  <p className="font-medium">
                    {selectedRequest.created_at ? new Date(selectedRequest.created_at).toLocaleString() : '-'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Requested At</p>
                  <p className="font-medium">
                    {selectedRequest.requested_at ? new Date(selectedRequest.requested_at).toLocaleString() : '-'}
                  </p>
                </div>
              </div>
              
              <div>
                <h4 className="font-medium mb-2">Bundles ({selectedRequest.bundle_codes?.length || 0})</h4>
                <div className="max-h-60 overflow-y-auto border rounded-lg p-3 bg-white">
                  <div className="flex flex-wrap gap-2">
                    {selectedRequest.bundle_codes?.map((bundle, idx) => (
                      <Badge key={idx} variant="outline" className="font-mono">
                        {bundle}
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowDetailDialog(false)}>
              Close
            </Button>
            {selectedRequest?.status === 'requested' && (
              <Button 
                className="bg-green-600 hover:bg-green-700"
                onClick={() => handleConfirmShipment(selectedRequest.transfer_id)}
                disabled={confirmingShipment === selectedRequest.transfer_id}
              >
                {confirmingShipment === selectedRequest.transfer_id ? (
                  <>Confirming...</>
                ) : (
                  <>
                    <Truck className="w-4 h-4 mr-1" />
                    Confirm Shipment
                  </>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GoodsTransferShipmentRequests;
